<template>
  <div class="flex flex-col gap-4">
    <div v-if="showGeneratedRevenue" class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <DateRangeFilterPicker
        v-model:start-date="startDate"
        v-model:end-date="endDate"
        :placeholder="t('dateRange')"
      />
      <Select v-model="statusFilter" :options="statusFilterOptions" variant="ghost" />
    </div>
    <LabeledText
      v-if="showGeneratedRevenue"
      :label="t('generatedRevenue')"
      :info-text="t('generatedRevenueInfo')"
    >
      <span v-if="revenue != null" v-currency="revenue" />
      <Spinner v-else class="size-4" />
    </LabeledText>
    <BookingsList :query="query" :empty-text="t('noBookings')" :show-menu="false" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import BookingsList from '../../bookings/bookings-list/BookingsList.vue';
import DateRangeFilterPicker from '@/components/DateRangeFilterPicker.vue';
import LabeledText from '@/components/LabeledText.vue';
import Select from '@/components/Select.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import { FindAllBookingsSort } from '@/entities/bookings/find-all-bookings-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import type { FindAllBookingsQueryDto } from '@/entities/bookings/booking.entity';
import { useCouponGeneratedRevenue } from '@/queries/use-coupons';

enum CouponBookingStatusFilter {
  ALL_EXCEPT_CANCELED = 'ALL_EXCEPT_CANCELED',
  AT_LEAST_CONFIRMED = 'AT_LEAST_CONFIRMED',
  RETURNED = 'RETURNED',
}

const STATUSES_BY_FILTER: Record<CouponBookingStatusFilter, BookingStatus[]> = {
  [CouponBookingStatusFilter.ALL_EXCEPT_CANCELED]: Object.values(BookingStatus).filter(
    (status) => status !== BookingStatus.CANCELED,
  ),
  [CouponBookingStatusFilter.AT_LEAST_CONFIRMED]: [
    BookingStatus.CONFIRMED,
    BookingStatus.HANDED_OVER,
    BookingStatus.CAR_RETURNED,
    BookingStatus.FINALIZED,
  ],
  [CouponBookingStatusFilter.RETURNED]: [BookingStatus.CAR_RETURNED, BookingStatus.FINALIZED],
};

const props = withDefaults(
  defineProps<{
    couponId: string;
    showGeneratedRevenue?: boolean;
  }>(),
  { showGeneratedRevenue: false },
);

const { t } = useI18n();
const startDate = ref<Date | null>(null);
const endDate = ref<Date | null>(null);
const statusFilter = ref(CouponBookingStatusFilter.ALL_EXCEPT_CANCELED);

const statusFilterOptions = computed(() => [
  {
    label: t('allExceptCanceled'),
    value: CouponBookingStatusFilter.ALL_EXCEPT_CANCELED,
  },
  {
    label: t('atLeastConfirmed'),
    value: CouponBookingStatusFilter.AT_LEAST_CONFIRMED,
  },
  { label: t('returned'), value: CouponBookingStatusFilter.RETURNED },
]);

const dateFilter = computed(() => ({
  ...(startDate.value
    ? { createdDateAfter: DateTime.fromJSDate(startDate.value).startOf('day').toISO() ?? undefined }
    : {}),
  ...(endDate.value
    ? { createdDateBefore: DateTime.fromJSDate(endDate.value).endOf('day').toISO() ?? undefined }
    : {}),
}));

const statuses = computed(() => STATUSES_BY_FILTER[statusFilter.value]);

const revenueQuery = computed(() => ({
  status: statuses.value,
  ...dateFilter.value,
}));

const { data } = useCouponGeneratedRevenue(
  props.couponId,
  revenueQuery,
  () => props.showGeneratedRevenue,
);
const revenue = computed(() => data.value?.generatedRevenue);

const query = computed<FindAllBookingsQueryDto>(() => ({
  filter: [
    {
      couponIds: [props.couponId],
      status: statuses.value,
      ...dateFilter.value,
    },
  ],
  sort: [FindAllBookingsSort.CREATED_DATE],
  order: Order.DESC,
}));
</script>

<i18n lang="json">
{
  "en": {
    "generatedRevenue": "Revenue generated",
    "generatedRevenueInfo": "Sum of the booking totals matching the selected filters.",
    "noBookings": "No bookings have used this coupon yet",
    "dateRange": "Redemption date",
    "allExceptCanceled": "All bookings (except cancelled)",
    "atLeastConfirmed": "At least confirmed",
    "returned": "Returned"
  },
  "de": {
    "generatedRevenue": "Erzeugter Umsatz",
    "generatedRevenueInfo": "Summe der Buchungsumsätze der gefilterten Einlösungen.",
    "noBookings": "Dieser Gutschein wurde noch nicht eingelöst",
    "dateRange": "Einlösedatum",
    "allExceptCanceled": "Alle Buchungen (außer stornierte)",
    "atLeastConfirmed": "Mindestens bestätigt",
    "returned": "Zurückgegeben"
  },
  "es": {
    "generatedRevenue": "Ingresos generados",
    "generatedRevenueInfo": "Suma de los importes de las reservas que coinciden con los filtros.",
    "noBookings": "Este cupón aún no se ha utilizado",
    "dateRange": "Fecha de canje",
    "allExceptCanceled": "Todas las reservas (excepto canceladas)",
    "atLeastConfirmed": "Al menos confirmada",
    "returned": "Devuelta"
  }
}
</i18n>
